<template>
  <div class="onevr-container" @click="handleOutsideClick">
    <!-- Back Button -->
    <button class="back-btn" @click.stop="goBack()" aria-label="Back">
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <circle cx="11" cy="11" r="11" fill="#111" />
        <path d="M13.5 16L9 11L13.5 6" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <img class="onevr-logo" :src="oneVRDarkBackLogo" alt="OneVR Logo" />

    <!-- Language Buttons -->
    <div class="language-toggle">
      <v-btn class="lang-btn" icon @click.stop="setLanguage('en')" color="black" elevation="4">
        <img :src="usa_flag" alt="EN" />
      </v-btn>
      <v-btn class="lang-btn" icon @click.stop="setLanguage('de')" color="black" elevation="4">
        <img :src="german_flag" alt="DE" />
      </v-btn>
    </div>

    <!-- App Selection -->
    <div class="card-row">
      <div class="app-card" :class="{ selected: selectedApp?.name === apps[0].name }" @click.stop="selectApp(apps[0])">
        <div class="app-image-container">
          <img :src="apps[0].icon" :alt="apps[0].name" style="margin-bottom:-1.5rem" />
        </div>
        <div class="app-label" :class="apps[0].class">{{ apps[0].name }}</div>
      </div>
      <div class="app-card" :class="{ selected: selectedApp?.name === apps[1].name }" @click.stop="selectApp(apps[1])">
        <div class="app-image-container">
          <img :src="apps[1].icon" :alt="apps[1].name" style="margin-bottom:-2.5rem;width:5rem" />
        </div>
        <div class="app-label" :class="apps[1].class">{{ apps[1].name }}</div>
      </div>
      <div class="app-card" :class="{ selected: selectedApp?.name === apps[2].name }" @click.stop="selectApp(apps[2])">
        <div class="app-image-container">
          <img :src="apps[2].icon" :alt="apps[2].name" />
        </div>
        <div class="app-label" :class="apps[2].class">{{ apps[2].name }}</div>
      </div>
    </div>

    <!-- Launch Button -->
    <transition name="fade">
      <div v-if="selectedApp" class="app-info" style="text-align: center;">
        <button class="jump-btn" @click.stop="openApp">{{ $t('app.jumpIn') }}</button>
      </div>
    </transition>

    <!-- Feature Cards -->
    <transition name="fade">
      <div class="extra-cards">
        <v-card class="image-card" v-for="card in cards" :key="card.label" elevation="8" hover
          @click.stop="card.action">
          <img :src="card.image" class="image-card-img" />
          <div class="image-label">{{ $t(card.label) }}</div>
        </v-card>
      </div>
    </transition>

    <!-- Dialog Components -->
    <OutsideBuildingView ref="buildingViewRef" />
    <FloorPlan3D ref="floorPlan3DRef" />
    <FloorPlan2D ref="simpleMapViewRef" />
    <FloorModel3D ref="floorModel3DRef" />
    <ConnectivityDiagram ref="connectivityRef" />
    <BookingBrief ref="bookingBriefRef" />
    <AnalyticsBrief ref="analyticsRef" />
    <ClientSegments ref="clientSegmentsRef" />
    <HardwareControlBrief ref="hardwareControlBriefRef" />
    <Operations ref="operationsBriefRef" />
    <Security ref="securityRef" />
    <Finance ref="financeRef" />
    <GeminiChatbot ref="geminiRef" />
  </div>
</template>

<style scoped></style>

<script setup>
import { ref, onMounted, onBeforeUnmount, defineExpose } from 'vue';
import { Device } from '@capacitor/device';
import { Browser } from '@capacitor/browser';
import { InAppBrowser } from '@capgo/inappbrowser';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import OutsideBuildingView from './OutsideBuildingView.vue';
import FloorPlan3D from './FloorPlan3D.vue';
import FloorPlan2D from './FloorPlan2D.vue';
import BookingBrief from './BookingBrief.vue';
import FloorModel3D from './FloorModel3D.vue';
import ConnectivityDiagram from './ConnectivityDiagram.vue';
import AnalyticsBrief from './AnalyticsBrief.vue';
import ClientSegments from './ClientSegments.vue';
import HardwareControlBrief from './HardwareControlBrief.vue';
import Operations from './Operations.vue';
import Security from './Security.vue';
import Finance from './Finance.vue';
import GeminiChatbot from './GeminiChatbot.vue';
import pia from '@/oneVr/oneVrAssets/pia.png';
import hbc from '@/oneVr/oneVrAssets/hbc.png';
import artinvest from '@/oneVr/oneVrAssets/artinvest.png';
import oneVRDarkBackLogo from '@/oneVr/oneVrAssets/oneVRDarkBackLogo.png';
import usa_flag from '@/oneVr/oneVrAssets/usa_flag.svg';
import german_flag from '@/oneVr/oneVrAssets/german_flag.svg';

const { locale } = useI18n();
const selectedApp = ref(null);
const currentLanguage = ref('en');

// Refs
const buildingViewRef = ref(null);
const connectivityRef = ref(null);
const floorPlan3DRef = ref(null);
const simpleMapViewRef = ref(null);
const floorModel3DRef = ref(null);
const bookingBriefRef = ref(null);
const analyticsRef = ref(null);
const clientSegmentsRef = ref(null);
const hardwareControlBriefRef = ref(null);
const operationsBriefRef = ref(null);
const securityRef = ref(null);
const financeRef = ref(null);
const geminiRef = ref(null);

// App List
const apps = [
  { name: 'PIA', icon: pia, url: 'https://pia.qa.pinestack.eu/', class: 'pia-neon', disabled: false },
  { name: 'HBC', icon: hbc, url: 'https://hbc.qa.pinestack.eu/', class: 'hbc-neon', disabled: false },
  { name: 'ART-INVEST', icon: artinvest, url: 'https://art-invest.showroom.pinestack.eu/', class: 'art-invest-neon', disabled: false },
];
const goBack = () => {
  window.history.back();
}
// Feature Cards
const cards = [
  { image: require('@/oneVr/oneVrAssets/building3d.png'), label: 'app.3dBuilding', action: () => buildingViewRef.value?.openBuilding?.() },
  { image: require('@/oneVr/oneVrAssets/FloorPlan2D.png'), label: 'app.2dFloorPlan', action: () => simpleMapViewRef.value?.openMap?.() },
  { image: require('@/oneVr/oneVrAssets/floorPlan3d.png'), label: 'app.3dFloorPlan', action: () => floorPlan3DRef.value?.openBuilding?.() },
  { image: require('@/oneVr/oneVrAssets/floorModel3d.png'), label: 'app.3dModel', action: () => floorModel3DRef.value?.openDialog?.() },
  { image: require('@/oneVr/oneVrAssets/connectivity3.png'), label: 'app.platformConnectivity', action: () => connectivityRef.value?.openDialog?.() },
  { image: require('@/oneVr/oneVrAssets/calendar.png'), label: 'app.booking', action: () => bookingBriefRef.value?.open?.() },
  { image: require('@/oneVr/oneVrAssets/chart.png'), label: 'app.analytics', action: () => analyticsRef.value?.open?.() },
  { image: require('@/oneVr/oneVrAssets/chart.png'), label: 'app.clientSegments', action: () => clientSegmentsRef.value?.open?.() },
  { image: require('@/oneVr/oneVrAssets/hardware.png'), label: 'app.hardware', action: () => hardwareControlBriefRef.value?.open?.() },
  { image: require('@/oneVr/oneVrAssets/operations.png'), label: 'app.operations', action: () => operationsBriefRef.value?.open?.() },
  { image: require('@/oneVr/oneVrAssets/security.png'), label: 'app.security', action: () => securityRef.value?.open?.() },
  { image: require('@/oneVr/oneVrAssets/finance.png'), label: 'app.finance', action: () => financeRef.value?.open?.() },
  { image: require('@/oneVr/oneVrAssets/chatbot.png'), label: 'app.assistant', action: () => geminiRef.value?.open?.() }
];

function selectApp(app) {
  selectedApp.value = app;
}

function setLanguage(lang) {
  locale.value = lang;
  localStorage.setItem('lang', lang);
  document.documentElement.setAttribute('lang', lang);
}

function handleOutsideClick(event) {
  if (!event.target.closest('.app-card')) {
    selectedApp.value = null;
  }
}

function authenticateGrafana() {
  const url =
    'https://auth.rom-bc.de/realms/aedifion/protocol/openid-connect/auth' +
    '?client_id=grafana-bc-live' +
    '&redirect_uri=https%3A%2F%2Fdashboard.rom-bc.de%2Fbc-live%2Flogin%2Fgeneric_oauth' +
    '&response_type=code' +
    '&scope=openid+email+profile+offline_access+roles' +
    '&state=jump-to-dashboard';

  window.location.href = url;
}


async function openApp() {
  if (!selectedApp.value) return;
  const { platform } = await Device.getInfo();
  if (platform === 'web') {
    await Browser.open({ url: selectedApp.value.url });
  } else {
    await InAppBrowser.openWebView({
      url: selectedApp.value.url,
      title: selectedApp.value.name,
      toolbarType: 'navigation',
      visibleTitle: true,
      backgroundColor: 'black',
      toolbarColor: '#1a1a1a',
      showReloadButton: true
    });
  }
}

// Handle screen resize/orientation
const resetLayout = () => {
  document.documentElement.style.transform = 'scale(1)';
  window.scrollTo(0, 0);
  floorPlan3DRef.value?.resetSvg?.();
  simpleMapViewRef.value?.closeMap?.();
  floorModel3DRef.value?.closeDialog?.();
};

const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
};

const handleOrientationChange = debounce(resetLayout, 100);

onMounted(() => {
  window.addEventListener('resize', handleOrientationChange);
  window.addEventListener('orientationchange', handleOrientationChange);
  resetLayout();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleOrientationChange);
  window.removeEventListener('orientationchange', handleOrientationChange);
  refreshPage()
});

// Add JS refresh page function
function refreshPage() {
  window.location.reload();
}
</script>

<style scoped>
.onevr-container {
  background: radial-gradient(circle, #0a0a0a, #000);
  color: white;
  min-height: 100vh;
  padding: 2vh 3vw;
  font-family: 'Segoe UI', sans-serif;
  box-sizing: border-box;
  overflow-y: auto;
  position: relative;
}

.back-btn {
  position: absolute;
  top: 08px;
  left: 16px;
  z-index: 20;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  outline: none;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: filter 0.2s;
}

.back-btn:hover {
  filter: brightness(1.3);
}

.onevr-logo {
  width: 100%;
  max-width: 240px;
  display: block;
  margin: 20px auto 10px;
  filter: brightness(1.2) saturate(1.2);
}

.language-toggle {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 12px;
  z-index: 10;
}

.lang-btn img {
  width: 36px;
  height: 26px;
  border-radius: 6px;

  box-shadow: 0 2px 6px rgba(255, 255, 255, 0.2);
}

.card-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
}

.app-card {
  width: 120px;
  min-height: 140px;
  background: #1e1e1e;
  border-radius: 18px;
  box-shadow: 0 4px 12px rgba(0, 191, 255, 0.08);
  text-align: center;
  transition: 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 10px;
}

.app-card:hover,
.app-card.selected {
  border: 2px solid #00bfff;
  transform: scale(1.05);
}

.app-image-container img {
  width: 60%;
  height: auto;
  object-fit: contain;
}



.app-label {
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  word-break: break-word;
  margin-top: 8px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pia-neon {
  color: #00bfff;
}

.hbc-neon {
  color: #ffffff;
}

.art-invest-neon {
  color: #00ff00;
}

.sms-neon {
  color: #ff4d4d;
}

.app-info {
  margin-top: 24px;
}

.jump-btn {
  padding: 10px 22px;
  font-size: 0.95rem;
  border-radius: 25px;
  background: linear-gradient(135deg, #00bfff, #0072ff);
  color: white;
  border: none;
  cursor: pointer;
  animation: breathe 2s ease-in-out infinite;
}

@keyframes breathe {

  0%,
  100% {
    box-shadow: 0 0 10px rgba(0, 123, 255, 0.3);
  }

  50% {
    box-shadow: 0 0 20px rgba(0, 123, 255, 0.7);
  }
}

.extra-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-top: 5vh;
  padding-bottom: 2vh;
}

.image-card {
  background: #1a1a1a;
  border-radius: 16px;
  transition: 0.3s ease;
  overflow: hidden;
  box-shadow: 0 6px 14px rgba(0, 123, 255, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.image-card:hover {
  transform: translateY(-5px) scale(1.02);
}

.image-card-img {
  width: 100%;
  max-height: 120px;
  object-fit: contain;
  padding: 10px;
}

.image-label {
  padding: 10px 8px;
  text-align: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: white;
  background: #111;
  word-break: break-word;
  min-height: 38px;
}

@media (max-width: 768px) {
  .app-card {
    width: 100px;
    min-height: 130px;
  }

  .app-label {
    font-size: 0.72rem;
    min-height: 30px;
  }

  .jump-btn {
    font-size: 0.85rem;
    padding: 8px 18px;
  }

  .image-card-img {
    max-height: 100px;
    padding: 6px;
  }

  .image-label {
    font-size: 0.7rem;
    min-height: 36px;
  }

  .extra-cards {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 12px;
  }

  /* ✅ Force app cards into one scrollable row on iPhones */
  @media (max-width: 430px) {
    .card-row {
      flex-wrap: wrap;
      justify-content: center;
      gap: 12px;
      margin-top: 20px;
    }

    .onevr-logo {
      margin-left: 0 !important;
      margin-right: auto !important;
      display: block;
      text-align: left;
    }

    .app-card {
      width: 80px;
      min-height: 100px;
      padding: 8px;
    }

    .app-label {
      font-size: 0.65rem;
      min-height: 22px;
    }

    .app-image-container img {
      width: 48px;
    }
  }

  /* ✅ Feature cards adjust for narrow screens and landscape mode */
  @media (max-width: 500px),
  (max-height: 450px) {

    .card-row {
      flex-wrap: wrap;
      justify-content: center;
      gap: 12px;
      margin-top: 20px;
    }


    .app-card {
      width: 80px;
      min-height: 100px;
      padding: 8px;
    }

    .extra-cards {
      grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
      gap: 10px;
      padding: 0 2vw 4vh;
    }

    .image-card-img {
      max-height: 80px;
      padding: 6px;
    }

    .image-label {
      font-size: 0.65rem;
      min-height: 28px;
    }

    .jump-btn {
      font-size: 0.75rem;
      padding: 6px 14px;
    }
  }




}
</style>
